import { AxiosError } from 'axios';

import { AXIOS_INSTANCE } from '../../services/api-client';
import {
  BVBrandVoice,
  BVBrandVoiceDraft,
  BVBootstrapSeedRequest,
  BVBootstrapSeedResponse,
  BVBootstrapStatus,
  BVCompetitorSet,
  BVCompetitorSetDraft,
  BVIndexedBrand,
  BVIndexedProduct,
  BVInsightsWorkspace,
  BVLibraryFilters,
  BVLibraryListResponse,
  BVLibraryVersionsResponse,
  BVStudioCompetitorContext,
  BVStudioCompetitorContextRequest,
  BVStudioDraft,
  BVStudioOutput,
  BVStudioSaveEditsRequest,
  BVStudioStatus,
} from './types';
import { normalizeStudioDraftForRequest } from './utils';

interface ListResponse<T> {
  items: T[];
}

function cleanParams<T extends object>(params: T) {
  return Object.fromEntries(
    Object.entries(params as Record<string, unknown>).filter(
      ([, value]) => value !== '' && value !== null && value !== undefined,
    ),
  );
}

function parseFilename(dispositionHeader: string | undefined, fallback: string): string {
  const rawFilename = dispositionHeader?.match(/filename="([^"]+)"/)?.[1];
  return rawFilename || fallback;
}

export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof AxiosError) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
}

export async function listIndexedBrands(limit = 200): Promise<BVIndexedBrand[]> {
  const response = await AXIOS_INSTANCE.get<ListResponse<BVIndexedBrand>>('/v1/bv-reviews/settings/brands', {
    params: { limit },
  });
  return response.data.items;
}

export async function listIndexedBrandProducts(
  brand: string,
  limit = 500,
): Promise<BVIndexedProduct[]> {
  const response = await AXIOS_INSTANCE.get<ListResponse<BVIndexedProduct>>(
    `/v1/bv-reviews/settings/brands/${encodeURIComponent(brand)}/products`,
    {
      params: { limit },
    },
  );
  return response.data.items;
}

export async function listCompetitorSets(): Promise<BVCompetitorSet[]> {
  const response = await AXIOS_INSTANCE.get<ListResponse<BVCompetitorSet>>(
    '/v1/bv-reviews/settings/competitor-sets',
  );
  return response.data.items;
}

export async function createCompetitorSet(payload: BVCompetitorSetDraft): Promise<BVCompetitorSet> {
  const response = await AXIOS_INSTANCE.post<BVCompetitorSet>('/v1/bv-reviews/settings/competitor-sets', payload);
  return response.data;
}

export async function updateCompetitorSet(
  setId: number,
  payload: BVCompetitorSetDraft,
): Promise<BVCompetitorSet> {
  const response = await AXIOS_INSTANCE.put<BVCompetitorSet>(
    `/v1/bv-reviews/settings/competitor-sets/${setId}`,
    payload,
  );
  return response.data;
}

export async function deleteCompetitorSet(setId: number): Promise<void> {
  await AXIOS_INSTANCE.delete(`/v1/bv-reviews/settings/competitor-sets/${setId}`);
}

export async function listBrandVoices(): Promise<BVBrandVoice[]> {
  const response = await AXIOS_INSTANCE.get<ListResponse<BVBrandVoice>>('/v1/bv-reviews/settings/brand-voice');
  return response.data.items;
}

export async function createBrandVoice(payload: BVBrandVoiceDraft): Promise<BVBrandVoice> {
  const response = await AXIOS_INSTANCE.post<BVBrandVoice>('/v1/bv-reviews/settings/brand-voice', payload);
  return response.data;
}

export async function updateBrandVoice(
  configId: number,
  payload: BVBrandVoiceDraft,
): Promise<BVBrandVoice> {
  const response = await AXIOS_INSTANCE.put<BVBrandVoice>(
    `/v1/bv-reviews/settings/brand-voice/${configId}`,
    payload,
  );
  return response.data;
}

export async function deleteBrandVoice(configId: number): Promise<void> {
  await AXIOS_INSTANCE.delete(`/v1/bv-reviews/settings/brand-voice/${configId}`);
}

export async function getBVInsightsWorkspace(): Promise<BVInsightsWorkspace> {
  const response = await AXIOS_INSTANCE.get<BVInsightsWorkspace>('/v1/ragflow/domains/by-name/bv_reviews');
  return response.data;
}

export async function getBVBootstrapStatus(): Promise<BVBootstrapStatus> {
  const response = await AXIOS_INSTANCE.get<BVBootstrapStatus>('/v1/bv-reviews/admin/status');
  return response.data;
}

export async function seedBVWorkspaceAndIngestion(
  request: BVBootstrapSeedRequest,
): Promise<BVBootstrapSeedResponse> {
  const response = await AXIOS_INSTANCE.post<BVBootstrapSeedResponse>('/v1/bv-reviews/admin/seed', request);
  return response.data;
}

export async function getStudioOutput(outputId: number): Promise<BVStudioOutput> {
  const response = await AXIOS_INSTANCE.get<BVStudioOutput>(`/v1/bv-reviews/studio/${outputId}`);
  return response.data;
}

export async function generateStudioOutput(draft: BVStudioDraft): Promise<BVStudioOutput> {
  const payload = normalizeStudioDraftForRequest(draft);
  const response = await AXIOS_INSTANCE.post<BVStudioOutput>('/v1/bv-reviews/studio/generate', payload);
  return response.data;
}

export async function getStudioCompetitorContext(
  payload: BVStudioCompetitorContextRequest,
): Promise<BVStudioCompetitorContext> {
  const response = await AXIOS_INSTANCE.post<BVStudioCompetitorContext>(
    '/v1/bv-reviews/studio/competitor-context',
    cleanParams(payload),
  );
  return response.data;
}

export async function regenerateStudioOutput(outputId: number, draft: BVStudioDraft): Promise<BVStudioOutput> {
  const payload = normalizeStudioDraftForRequest(draft);
  const response = await AXIOS_INSTANCE.post<BVStudioOutput>(
    `/v1/bv-reviews/studio/${outputId}/regenerate`,
    payload,
  );
  return response.data;
}

export async function saveStudioOutputEdits(
  outputId: number,
  payload: BVStudioSaveEditsRequest,
): Promise<BVStudioOutput> {
  const response = await AXIOS_INSTANCE.patch<BVStudioOutput>(`/v1/bv-reviews/studio/${outputId}`, payload);
  return response.data;
}

export async function updateStudioStatus(
  outputId: number,
  status: BVStudioStatus,
  rejectionReason?: string,
): Promise<BVStudioOutput> {
  const response = await AXIOS_INSTANCE.patch<BVStudioOutput>(`/v1/bv-reviews/studio/${outputId}/status`, {
    status,
    rejection_reason: rejectionReason ?? null,
  });
  return response.data;
}

export async function listLibraryOutputs(filters: BVLibraryFilters): Promise<BVLibraryListResponse> {
  const response = await AXIOS_INSTANCE.get<BVLibraryListResponse>('/v1/bv-reviews/library', {
    params: cleanParams(filters),
  });
  return response.data;
}

export async function getLibraryOutput(outputId: number): Promise<BVStudioOutput> {
  const response = await AXIOS_INSTANCE.get<BVStudioOutput>(`/v1/bv-reviews/library/${outputId}`);
  return response.data;
}

export async function listLibraryVersions(outputId: number): Promise<BVLibraryVersionsResponse> {
  const response = await AXIOS_INSTANCE.get<BVLibraryVersionsResponse>(`/v1/bv-reviews/library/${outputId}/versions`);
  return response.data;
}

export async function duplicateLibraryOutput(outputId: number): Promise<BVStudioOutput> {
  const response = await AXIOS_INSTANCE.post<BVStudioOutput>(`/v1/bv-reviews/library/${outputId}/duplicate`);
  return response.data;
}

export async function deleteLibraryOutput(outputId: number): Promise<void> {
  await AXIOS_INSTANCE.delete(`/v1/bv-reviews/library/${outputId}`);
}

export async function exportStudioOutputPdf(outputId: number): Promise<{ blob: Blob; filename: string }> {
  const response = await AXIOS_INSTANCE.post(`/v1/bv-reviews/studio/${outputId}/export`, undefined, {
    responseType: 'blob',
  });

  return {
    blob: response.data as Blob,
    filename: parseFilename(response.headers['content-disposition'], `bv-studio-output-${outputId}.pdf`),
  };
}
